import React, { FC, ReactNode, useEffect, useState } from 'react';
import styles from './response.module.scss';
import useThread from '@/pages/Thread/hooks/useThread';
import useItem from '@/pages/Thread/hooks/useItem';
import SourceIndicator from '../../SourceIndicator';
import CitationModal from '../CitationsModal';
import useTypingEffect from '@/pages/Thread/hooks/useTypingEffect';
import { CITATION_WRAPPER_ATTRIBUTE } from '@/utils/misc';
import Markdown from '../../Markdown';
import { MarkdownComponent, MarkdownComponentNode } from '@/models/markdown';

const Response = () => {
  const { status } = useThread();
  const { item, isLast } = useItem();
  const { response, sources } = item;
  const [selectedChunk, setSelectedChunk] = useState<number | null>(null);
  const isTyping = isLast && status === 'TYPING';
  const { chunks, setChunks } = useTypingEffect(isTyping, response);

  useEffect(() => {
    if (status === 'READY' || status === 'CITING') {
      setChunks(response);
    }
  }, [response, setChunks, status]);

  useEffect(() => {
    if (isTyping && chunks.length === 0 && response.length > 0) {
      setChunks([
        {
          text: '',
          citation: response[0].citation,
        },
      ]);
    }
  }, [chunks.length, isTyping, response, setChunks]);

  return (
    <div
      className={styles['response']}
      data-typing={isLast && status === 'TYPING'}
    >
      {chunks.length === 0 && <p className={styles['response-part']}></p>}
      <div className={styles['response-part']}>
        {chunks.map((chunk, i) => {
          const { text, citation } = chunk;
          const citationEntries = Array.isArray(citation)
            ? citation
                .map(({ source_id }) => ({
                  chunk: i,
                  source: source_id,
                }))
                .filter(
                  ({ chunk, source }) =>
                    chunk !== undefined && source !== undefined
                )
            : [];

          const withSourceIndicators =
            (Tag: keyof JSX.IntrinsicElements) =>
            ({
              children,
              node,
            }: {
              children: ReactNode;
              node: MarkdownComponentNode;
            }) => {
              const renderCitations =
                Array.isArray(citationEntries) &&
                typeof node?.position?.end?.offset !== 'undefined' &&
                (Tag === 'table' ||
                  !Array.isArray(children) ||
                  typeof (children as Array<{ type: string }>).find(
                    ({ type }) => ['ul', 'ol'].includes(type)
                  ) === 'undefined') &&
                node?.position?.end?.offset === text.length;
              return (
                <Tag>
                  {children}
                  {renderCitations &&
                    (Tag === 'table' ? (
                      <tfoot>
                        <tr>
                          <td colSpan={100}>
                            <span>Sources:</span>
                            <div>
                              <SourceIndicators entries={citationEntries} />
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    ) : (
                      <SourceIndicators entries={citationEntries} />
                    ))}
                </Tag>
              );
            };

          const SourceIndicators: FC<{
            entries: { chunk: number; source: number }[];
          }> = ({ entries }) => {
            return (
              <>
                {entries.map(({ chunk, source }, i) => (
                  <SourceIndicator
                    key={i}
                    id={source}
                    onClick={() => setSelectedChunk(chunk)}
                  />
                ))}
              </>
            );
          };

          const componentRenderMapping: Record<
            string,
            FC<MarkdownComponent>
          > = {
            h1: withSourceIndicators('h4'),
            h2: withSourceIndicators('h5'),
            h3: withSourceIndicators('h6'),
            h4: withSourceIndicators('h6'),
            h5: withSourceIndicators('h6'),
            h6: withSourceIndicators('h6'),
            p: withSourceIndicators('p'),
            strong: withSourceIndicators('strong'),
            em: withSourceIndicators('em'),
            li: withSourceIndicators('li'),
            table: ({ children, node }) => (
              <div className={styles['table-container']}>
                {withSourceIndicators('table')({ children, node })}
              </div>
            ),
          };

          return (
            <div key={i} {...CITATION_WRAPPER_ATTRIBUTE}>
              <Markdown content={text} components={componentRenderMapping} />
            </div>
          );
        })}
      </div>

      {selectedChunk !== null && (
        <CitationModal
          chunk={chunks[selectedChunk]}
          sources={sources}
          onClose={() => setSelectedChunk(null)}
        />
      )}
    </div>
  );
};

export default Response;
